// PRD 10 §3.4 — category management slide panel. Lists categories with inline
// name editing + content counts, a "new category" add row, and per-row delete.
//
// Permissions: add / rename need CONTENT_LIB:WRITE; delete is Root-only
// (isRoot) AND blocked when the category still holds content. The panel is
// only reachable via the [Manage Categories] button (WRITE-gated), but every
// control re-checks defensively.

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';

import { useAuthStore } from '@/app/store/auth.store';
import { ConfirmModal } from '@/components/feedback/ConfirmModal';
import { SlidePanel } from '@/components/feedback/SlidePanel';
import { toast } from '@/components/feedback/useToast';
import { useWriteGuard, useWriteDenyGuard } from '@/shared/hooks/useWriteGuard';
import { hasEmoji } from '@/shared/utils/emoji';

import {
  useCreateCategory,
  useDeleteCategory,
  useUpdateCategory,
} from '../hooks/use-health-encyclopedia-mutations';
import { useCategoriesQuery } from '../hooks/use-health-encyclopedia';
import { useHealthEncyclopediaPageStore } from '../store/health-encyclopedia-page.store';
import type { Category } from '../types/health-encyclopedia.types';
import { getEncyclopediaErrorMessage } from '../utils/error-message';

const NAME_MAX = 20;

// One category row with inline rename. Local `draft` lets the user type freely;
// the rename commits on blur / Enter and reverts on validation/server error.
function CategoryRow({
  category,
  canWrite,
  canDelete,
  onRename,
  onRequestDelete,
}: {
  category: Category;
  canWrite: boolean;
  canDelete: boolean;
  onRename: (category: Category, name: string) => Promise<void>;
  onRequestDelete: (category: Category) => void;
}) {
  const { t } = useTranslation('health-encyclopedia');
  // SHCT-582 — PRD 21 §7.4 P-2. The row already showed the right tooltip on ✕; what it lacked was
  // one on the rename field and the marker the L2 guard (P-3) needs.
  const { denyProps } = useWriteDenyGuard(canWrite);
  const { denyProps: deleteDenyProps } = useWriteDenyGuard(canDelete);
  const [draft, setDraft] = useState(category.name);

  // Keep the field in sync if the canonical name changes underneath us
  // (e.g. another rename succeeded and the list refetched).
  const [renameError, setRenameError] = useState('');

  useEffect(() => {
    setDraft(category.name);
    setRenameError('');
  }, [category.name]);

  const commit = async () => {
    const name = draft.trim();
    if (name === category.name) return;
    if (!name) {
      setDraft(category.name);
      return;
    }
    // SHCT-855 — the inline rename shares the NAME-class emoji ban. Per CLAUDE.md
    // §7.1 a Validation Error is inline, so the row grew an error slot instead of
    // reverting silently; the typed value stays put so the user can fix it.
    if (hasEmoji(name)) {
      setRenameError(t('common:errors.emojiNotAllowed'));
      return;
    }
    setRenameError('');
    try {
      await onRename(category, name);
    } catch {
      setDraft(category.name);
    }
  };

  const hasContent = category.contentCount > 0;
  const deleteDisabled = !canDelete || hasContent;
  const deleteTitle = hasContent
    ? t('categoryManager.deleteTooltip')
    : !canDelete
      ? t('categoryManager.deleteTooltipNoPerm')
      : t('list.deleteRow');

  return (
    <div>
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: 8,
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-sm)',
        background: '#fff',
      }}
    >
      <input
        type="text"
        className={`form-field__input${renameError ? ' form-field__input--error' : ''}`}
        style={{ flex: 1 }}
        value={draft}
        maxLength={NAME_MAX}
        disabled={!canWrite}
        {...denyProps}
        aria-label={category.name}
        onChange={(e) => {
          setDraft(e.target.value);
          if (renameError) setRenameError('');
        }}
        onBlur={() => void commit()}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            e.currentTarget.blur();
          }
        }}
      />
      <span
        style={{
          fontSize: 12,
          color: 'var(--muted-foreground)',
          whiteSpace: 'nowrap',
          minWidth: 48,
          textAlign: 'right',
        }}
      >
        {t('categoryManager.count', { count: category.contentCount })}
      </span>
      <button
        type="button"
        className="btn btn-sm btn-ghost"
        aria-label={deleteTitle}
        disabled={deleteDisabled}
        style={{ color: canDelete ? 'var(--destructive)' : undefined, padding: '2px 8px' }}
        onClick={() => onRequestDelete(category)}
        {...deleteDenyProps}
        // `deleteTitle` already resolves to the §7.4 P-2 copy when the permission is what blocks
        // it, and to the 콘텐츠 존재 reason when that is the cause — which outranks it.
        title={deleteTitle}
      >
        ✕
      </button>
    </div>
      {renameError ? (
        <p className="form-field__error" style={{ margin: '4px 0 0' }}>
          {renameError}
        </p>
      ) : null}
    </div>
  );
}

export function CategoryManagerPanel() {
  const { t } = useTranslation('health-encyclopedia');
  const isRoot = useAuthStore((s) => s.isRoot);
  const { canWrite, denyProps } = useWriteGuard('CONTENT_LIB');

  const panel = useHealthEncyclopediaPageStore((s) => s.panel);
  const closePanel = useHealthEncyclopediaPageStore((s) => s.closePanel);
  const open = panel?.mode === 'category-manager';

  const categoriesQuery = useCategoriesQuery();
  const categories = categoriesQuery.data?.categories ?? [];

  const createMut = useCreateCategory();
  const updateMut = useUpdateCategory();
  const deleteMut = useDeleteCategory();

  const [newName, setNewName] = useState('');
  const [addError, setAddError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<Category | null>(null);

  // Reset transient state whenever the panel closes.
  useEffect(() => {
    if (!open) {
      setNewName('');
      setAddError('');
      setDeleteTarget(null);
    }
  }, [open]);

  const handleAdd = () => {
    setAddError('');
    const name = newName.trim();
    if (!name) {
      setAddError(t('errors.CATEGORY_NAME_REQUIRED'));
      return;
    }
    // SHCT-855 — 카테고리명 is a NAME-class field: 이모지 불허 ([공통] 00 §4.2).
    if (hasEmoji(name)) {
      setAddError(t('common:errors.emojiNotAllowed'));
      return;
    }
    createMut.mutate(
      { name },
      {
        onSuccess: () => {
          setNewName('');
          toast.success(t('categoryManager.toasts.added'));
        },
        onError: (e) =>
          setAddError(getEncyclopediaErrorMessage(e, t, 'categoryManager.toasts.addError')),
      },
    );
  };

  const handleRename = (category: Category, name: string): Promise<void> =>
    updateMut
      .mutateAsync({ id: category.id, input: { name } })
      .then(() => {
        toast.success(t('categoryManager.toasts.renamed'));
      })
      .catch((e) => {
        toast.error(getEncyclopediaErrorMessage(e, t, 'categoryManager.toasts.renameError'));
        throw e; // let the row revert its draft
      });

  const handleConfirmDelete = () => {
    if (!deleteTarget) return;
    deleteMut.mutate(deleteTarget.id, {
      onSuccess: () => {
        toast.success(t('categoryManager.toasts.deleted'));
        setDeleteTarget(null);
      },
      onError: (e) =>
        toast.error(getEncyclopediaErrorMessage(e, t, 'categoryManager.toasts.deleteError')),
    });
  };

  return (
    <>
      <SlidePanel open={open} title={t('categoryManager.title')} width={520} onClose={closePanel}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)', height: '100%' }}>
          <p style={{ fontSize: 13, color: 'var(--muted-foreground)', margin: 0 }}>
            {t('categoryManager.intro')}
          </p>

          <div style={{ display: 'flex', gap: 8 }}>
            <input
              type="text"
              className="form-field__input"
              style={{ flex: 1 }}
              value={newName}
              maxLength={NAME_MAX}
              disabled={!canWrite || createMut.isPending}
              {...denyProps}
              placeholder={t('categoryManager.newPlaceholder')}
              aria-label={t('categoryManager.newPlaceholder')}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAdd();
                }
              }}
            />
            <button
              type="button"
              className="btn btn-default btn-primary"
              disabled={!canWrite || createMut.isPending}
              onClick={handleAdd}
              {...denyProps}
            >
              <Plus size={16} />
              {t('categoryManager.addButton')}
            </button>
          </div>
          {addError ? (
            <p className="form-field__error" style={{ margin: 0 }}>
              {addError}
            </p>
          ) : null}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, overflow: 'auto', flex: 1 }}>
            {categories.length === 0 ? (
              <p style={{ fontSize: 13, color: 'var(--muted-foreground)', textAlign: 'center', padding: 16 }}>
                {t('categoryManager.empty')}
              </p>
            ) : (
              categories.map((category) => (
                <CategoryRow
                  key={category.id}
                  category={category}
                  canWrite={canWrite}
                  canDelete={isRoot}
                  onRename={handleRename}
                  onRequestDelete={setDeleteTarget}
                />
              ))
            )}
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              paddingTop: 'var(--space-4)',
              borderTop: '1px solid var(--border)',
            }}
          >
            <button type="button" className="btn btn-default btn-secondary" onClick={closePanel}>
              {t('categoryManager.close')}
            </button>
          </div>
        </div>
      </SlidePanel>

      <ConfirmModal
        open={Boolean(deleteTarget)}
        title={t('categoryManager.deleteConfirm.title')}
        message={t('categoryManager.deleteConfirm.message', { name: deleteTarget?.name ?? '' })}
        confirmLabel={t('categoryManager.deleteConfirm.confirm')}
        cancelLabel={t('categoryManager.deleteConfirm.cancel')}
        confirmVariant="destructive"
        loading={deleteMut.isPending}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </>
  );
}
